import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Save, ArrowLeft, CheckCircle, Search, ChevronDown, X } from 'lucide-react';
import { uploadMultipleImages } from '../services/cloudinary';
import { createPledge, getLoans, updateLoanCollateralStatus, getSettingsByType } from '../services/api';
import Toast from '../components/Toast';
import ImageSourcePicker from '../components/ImageSourcePicker';
import { useLanguage } from '../contexts/LanguageContext';

const isLoanClosedForRepledge = (loan) => {
  const status = (loan?.status || '').toUpperCase();
  const collateral = (loan?.collateralStatus || '').toUpperCase();
  return status === 'COMPLETED' && collateral === 'HANDOVER';
};

const isLoanAlreadyPledged = (loan) => {
  const collateral = (loan?.collateralStatus || '').toUpperCase();
  return collateral === 'PLEDGED';
};

const CreatePledge = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const texts = t('createPledge');
  
  const [formData, setFormData] = useState({
    loanId: '',
    pledgedTo: '',
    pledgeAmount: '',
    interestRate: '',
    pledgeDate: new Date().toISOString().split('T')[0]
  });
  
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanSearch, setLoanSearch] = useState('');
  const [loanSearchLoading, setLoanSearchLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Pledge settings state
  const [pledgeSettings, setPledgeSettings] = useState([]);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const loadingRef = useRef(false);
  const loanSearchTimeoutRef = useRef(null);
  const MIN_LOAN_SEARCH_CHARS = 2;

  // Fetch pledge settings
  const fetchPledgeSettings = useCallback(async () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        loadingRef.current = false;
        return;
      }

      const result = await getSettingsByType(businessId, 'PLEDGE');
      if (result.success && result.data) {
        setPledgeSettings(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error('Error fetching pledge settings:', error);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  const searchLoans = useCallback(async (term) => {
    const query = (term || '').trim();
    if (query.length < MIN_LOAN_SEARCH_CHARS) {
      setLoans([]);
      return;
    }

    setLoanSearchLoading(true);
    try {
      const result = await getLoans({
        page: 0,
        size: 20,
        search: query
      });

      if (result.success && result.data) {
        const loansData = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data.content)
            ? result.data.content
            : [];
        // Hide fully closed loans (COMPLETED + HANDOVER) from re-pledge search
        setLoans(loansData.filter((loan) => !isLoanClosedForRepledge(loan)));
      } else {
        setLoans([]);
      }
    } catch (err) {
      console.error('Error searching loans:', err);
      setLoans([]);
    } finally {
      setLoanSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPledgeSettings();
  }, [fetchPledgeSettings]);

  useEffect(() => {
    if (loanSearchTimeoutRef.current) {
      clearTimeout(loanSearchTimeoutRef.current);
    }

    const query = loanSearch.trim();
    if (query.length < MIN_LOAN_SEARCH_CHARS) {
      setLoans([]);
      return undefined;
    }

    loanSearchTimeoutRef.current = setTimeout(() => {
      searchLoans(query);
    }, 400);

    return () => {
      if (loanSearchTimeoutRef.current) {
        clearTimeout(loanSearchTimeoutRef.current);
      }
    };
  }, [loanSearch, searchLoans]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle bank selection from dropdown
  const handleBankSelect = (setting) => {
    setFormData(prev => ({
      ...prev,
      pledgedTo: setting.name,
      interestRate: setting.interestRate.toString()
    }));
    setShowBankDropdown(false);
    setBankSearchTerm('');
  };

  // Handle custom bank name input
  const handlePledgedToChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      pledgedTo: value
    }));
    setBankSearchTerm(value);
    setShowBankDropdown(true);
    
    // Auto-fill interest rate if exact match is found
    const matchedSetting = pledgeSettings.find(s => 
      s.name.toLowerCase() === value.toLowerCase()
    );
    if (matchedSetting) {
      setFormData(prev => ({
        ...prev,
        pledgedTo: value,
        interestRate: matchedSetting.interestRate.toString()
      }));
    } else {
      // Clear interest rate if no match
      setFormData(prev => ({
        ...prev,
        pledgedTo: value,
        interestRate: ''
      }));
    }
  };

  // Filter banks based on search
  const filteredBanks = pledgeSettings.filter(setting =>
    setting.name.toLowerCase().includes(bankSearchTerm.toLowerCase())
  );

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (images only)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setToast({ 
          show: true, 
          message: 'Please select valid image file (JPEG, PNG)', 
          type: 'error' 
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setToast({ 
          show: true, 
          message: `${file.name} is too large. Maximum size is 5MB`, 
          type: 'error' 
        });
        return;
      }
      
      setSelectedPhoto(file);
      setToast({ 
        show: true, 
        message: 'Photo selected', 
        type: 'success' 
      });
    }
  };

  const removePhoto = () => {
    setSelectedPhoto(null);
  };

  const alreadyPledgedMessage =
    texts?.messages?.alreadyPledged || 'The item is already pledged';

  const handleSelectLoan = (loan) => {
    if (isLoanClosedForRepledge(loan)) {
      setToast({
        show: true,
        message: texts?.messages?.loanClosed || 'This loan is completed and handed over',
        type: 'error'
      });
      setSelectedLoan(null);
      setLoanSearch('');
      return;
    }

    if (isLoanAlreadyPledged(loan)) {
      setToast({
        show: true,
        message: alreadyPledgedMessage,
        type: 'error'
      });
      setSelectedLoan(null);
      setLoanSearch('');
      return;
    }

    setSelectedLoan(loan);
    setLoanSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving || isUploading) return;
    
    if (!selectedLoan) {
      setToast({ 
        show: true, 
        message: texts?.messages?.selectLoan || 'Please select a loan', 
        type: 'error' 
      });
      return;
    }

    if (!formData.pledgeDate) {
      setToast({
        show: true,
        message: texts?.messages?.selectPledgeDate || 'Please select a pledge date',
        type: 'error'
      });
      return;
    }

      // Block only when loan collateral is currently at bank (IN_HAND can be re-pledged)
      if (isLoanClosedForRepledge(selectedLoan)) {
        setToast({
          show: true,
          message: texts?.messages?.loanClosed || 'This loan is completed and handed over',
          type: 'error'
        });
        return;
      }

      if (isLoanAlreadyPledged(selectedLoan)) {
        setToast({
          show: true,
          message: alreadyPledgedMessage,
          type: 'error'
        });
        return;
      }

      setIsSaving(true);

      try {
        let documentUrl = '';
      
      // STEP 1: Handle image upload if photo is selected
      if (selectedPhoto) {
        setIsUploading(true);
        
        try {
          const uploadedUrls = await uploadMultipleImages([selectedPhoto], { folder: 'pledge-documents' });
          
          if (uploadedUrls && uploadedUrls.length > 0) {
            documentUrl = uploadedUrls[0];
          }
          
          setToast({ 
            show: true, 
            message: 'Document uploaded successfully!', 
            type: 'success' 
          });
        } catch (uploadError) {
          setToast({ 
            show: true, 
            message: `Image upload failed: ${uploadError.message}. Please try again.`, 
            type: 'error' 
          });
          return;
        } finally {
          setIsUploading(false);
        }
      }

      // STEP 2: Create pledge
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ 
          show: true, 
          message: 'No business selected', 
          type: 'error' 
        });
        return;
      }

      // Use user-selected pledge date; maturity = 1 year after pledge date
      const pledgeDate = formData.pledgeDate || new Date().toISOString().split('T')[0];
      const pledgeDateObj = new Date(`${pledgeDate}T00:00:00`);
      const maturityDate = new Date(pledgeDateObj);
      maturityDate.setFullYear(maturityDate.getFullYear() + 1);
      const maturityDateString = maturityDate.toISOString().split('T')[0];

      const payload = {
        businessId,
        loanId: selectedLoan?.id || '',
        pledgedTo: formData.pledgedTo,
        pledgeAmount: parseFloat(formData.pledgeAmount),
        interestRate: parseFloat(formData.interestRate),
        pledgeDate,
        maturityDate: maturityDateString,
        documentUrl: documentUrl || 'string'
      };

      const result = await createPledge(payload);
      
      if (result.success) {
        // Update loan collateral status to PLEDGED
        try {
          await updateLoanCollateralStatus(selectedLoan?.id, 'PLEDGED');
        } catch (statusErr) {
          console.error('Failed to update loan status:', statusErr);
          // Don't fail the pledge creation if status update fails
        }
        
        setToast({ show: true, message: texts?.messages?.pledgeCreatedSuccess || 'Pledge created successfully', type: 'success' });
        setTimeout(() => {
          navigate('/collateral');
        }, 1500);
      } else {
        setToast({ 
          show: true, 
          message: result.message || texts?.messages?.createFailed || 'Failed to create pledge', 
          type: 'error' 
        });
      }
    } catch (err) {
      console.error('Error in process:', err);
      setToast({ show: true, message: err.message || 'Error saving pledge', type: 'error' });
    } finally {
      setIsSaving(false);
      setIsUploading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getLoanDisplayNumber = (loan) => loan?.loanNumber || texts?.labels?.na || 'N/A';
  const getBorrowerName = (loan) => loan?.name || loan?.borrowerName || texts?.labels?.na || 'N/A';
  const getBorrowerPhone = (loan) => loan?.borrowerContactNumber || loan?.contactNumber || '';
  const getSerialNumber = (loan) => (loan?.serialNumber || '').trim();
  const serialNumberLabel = texts.serialNumber || t('loans.tableHeaders.serialNumber') || 'Serial Number';

  const loanSearchTooShort = loanSearch.trim().length > 0 && loanSearch.trim().length < MIN_LOAN_SEARCH_CHARS;

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
          <button
            type="button"
            onClick={() => navigate('/collateral')}
            className="create-pledge-back"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="card-title" style={{ marginBottom: 0 }}>{texts.title || 'Create New Pledge'}</h2>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Left Column */}
            <div>
              <div className="form-group">
                <label className="form-label">{texts.loanNumber || texts.loanId || 'Loan Number'} *</label>
                <div className="search-field-wrap">
                  <div className="search-field" style={{ position: 'relative' }}>
                    <Search 
                      size={20} 
                      style={{ 
                        position: 'absolute', 
                        left: '0.75rem', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: 'var(--text-secondary)',
                        pointerEvents: 'none'
                      }} 
                    />
                    <input
                      type="text"
                      className={`form-input${loanSearchTooShort ? ' search-input--error' : ''}`}
                      placeholder={texts.loanIdPlaceholder || 'Search by loan number, customer name, or phone...'}
                      value={loanSearch}
                      onChange={(e) => setLoanSearch(e.target.value)}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                    {loanSearch && (
                      <button
                        type="button"
                        className="search-field-clear"
                        onClick={() => {
                          setLoanSearch('');
                          setLoans([]);
                        }}
                        aria-label="Clear search"
                        title="Clear"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {loanSearchTooShort && (
                    <p className="list-filter-search-error">
                      {t('loans.messages.searchMinChars') || 'Type at least 2 characters to search'}
                    </p>
                  )}
                </div>
              </div>

              {/* Loan search results from API */}
              {loanSearch.trim().length >= MIN_LOAN_SEARCH_CHARS && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div className="search-result-list" style={{ maxHeight: '250px' }}>
                    {loanSearchLoading ? (
                      <div className="search-result-empty">{texts.searchingLoans || 'Searching loans...'}</div>
                    ) : loans.length === 0 ? (
                      <div className="search-result-empty">{texts.noLoansFound || 'No loans found'}</div>
                    ) : (
                      loans.map((loan) => {
                        const alreadyPledged = isLoanAlreadyPledged(loan);
                        return (
                        <div
                          key={loan.id}
                          onClick={() => handleSelectLoan(loan)}
                          className={`search-result-item${selectedLoan?.id === loan.id ? ' is-selected' : ''}${alreadyPledged ? ' is-disabled' : ''}`}
                        >
                          <div className="search-result-title">
                            {getLoanDisplayNumber(loan)}
                          </div>
                          {getSerialNumber(loan) && (
                            <div className="search-result-meta" style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                              {serialNumberLabel}: {getSerialNumber(loan).toUpperCase()}
                            </div>
                          )}
                          <div className="search-result-meta">
                            {getBorrowerName(loan)}
                            {getBorrowerPhone(loan) ? ` · ${getBorrowerPhone(loan)}` : ''}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#34d399', fontWeight: '500' }}>
                            {texts.amount || 'Amount'}: {formatCurrency(loan.totalAdvanced ?? loan.finalDisbursedAmount)}
                          </div>
                          {alreadyPledged && (
                            <div style={{ fontSize: '0.8rem', color: '#f87171', fontWeight: '600', marginTop: '0.35rem' }}>
                              {alreadyPledgedMessage}
                            </div>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Selected Loan Display */}
              {selectedLoan && (
                <div className="create-pledge-selected-loan">
                  <div className="create-pledge-selected-loan-title">
                    <CheckCircle size={20} color="#34d399" />
                    <span>{texts.selectedLoan || 'Selected Loan'}</span>
                  </div>
                  <div className="create-pledge-selected-loan-row">
                    <strong>{texts.loanNumber || 'Loan Number'}:</strong> {getLoanDisplayNumber(selectedLoan)}
                  </div>
                  {getSerialNumber(selectedLoan) && (
                    <div className="create-pledge-selected-loan-row">
                      <strong>{serialNumberLabel}:</strong> {getSerialNumber(selectedLoan).toUpperCase()}
                    </div>
                  )}
                  <div className="create-pledge-selected-loan-row">
                    <strong>{texts.borrower || 'Borrower'}:</strong> {getBorrowerName(selectedLoan)}
                  </div>
                  {getBorrowerPhone(selectedLoan) && (
                    <div className="create-pledge-selected-loan-row">
                      <strong>{texts.phone || 'Phone'}:</strong> {getBorrowerPhone(selectedLoan)}
                    </div>
                  )}
                  <div className="create-pledge-selected-loan-row">
                    <strong>{texts.amount || 'Amount'}:</strong> {formatCurrency(selectedLoan.totalAdvanced ?? selectedLoan.finalDisbursedAmount)}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">{texts.pledgeAmount || 'Pledge Amount (₹)'} *</label>
                <input
                  type="number"
                  name="pledgeAmount"
                  className="form-input"
                  placeholder={texts.pledgeAmountPlaceholder || "Enter pledge amount"}
                  value={formData.pledgeAmount}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{texts.pledgeDate || 'Pledge Date'} *</label>
                <input
                  type="date"
                  name="pledgeDate"
                  className="form-input"
                  value={formData.pledgeDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{texts.uploadDocument || 'Upload Pledge Document'} ({texts.uploadDocumentHint || 'Optional'})</label>
                <div style={{ display: 'grid', gridTemplateColumns: selectedPhoto ? '1fr 1fr' : '1fr', gap: '1rem' }}>
                  <div
                    className={`id-proof-upload create-pledge-upload${selectedPhoto ? ' has-files' : ''}`}
                  >
                    <Upload size={32} className="id-proof-upload-icon" />
                    {selectedPhoto ? (
                      <div>
                        <div className="id-proof-upload-success">
                          ✓ Document selected
                        </div>
                        <div className="id-proof-upload-hint">
                          {selectedPhoto.name}
                        </div>
                        <div className="id-proof-photo-size" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                          {(selectedPhoto.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="id-proof-upload-muted" style={{ marginBottom: '0.75rem' }}>
                          {texts.clickToUpload || 'Upload document'}
                        </div>
                        <div className="id-proof-upload-sub" style={{ marginBottom: '0.75rem' }}>
                          {texts.supportedFormats || 'Supports: JPEG, PNG (Max 5MB)'}
                        </div>
                        <ImageSourcePicker
                          disabled={isUploading || !!selectedPhoto}
                          onChange={handlePhotoChange}
                          galleryLabel={t('common.gallery')}
                          cameraLabel={t('common.camera')}
                        />
                      </>
                    )}
                  </div>

                  {selectedPhoto && (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={URL.createObjectURL(selectedPhoto)}
                        alt="Pledge Document"
                        className="create-pledge-doc-preview"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="create-pledge-doc-remove"
                        title="Remove document"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">{texts.pledgedTo || 'Pledged To'} *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="pledgedTo"
                    className="form-input"
                    placeholder={texts.pledgedToPlaceholder || "Select or type bank name"}
                    value={formData.pledgedTo}
                    onChange={handlePledgedToChange}
                    onFocus={() => setShowBankDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click event on dropdown items
                      setTimeout(() => setShowBankDropdown(false), 200);
                    }}
                    required
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <ChevronDown 
                    size={20} 
                    style={{ 
                      position: 'absolute', 
                      right: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: 'var(--text-secondary)',
                      pointerEvents: 'none'
                    }} 
                  />
                  
                  {/* Dropdown */}
                  {showBankDropdown && pledgeSettings.length > 0 && (
                    <div className="search-result-list" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '0.25rem',
                      maxHeight: '200px',
                      zIndex: 1000,
                      boxShadow: 'var(--surface-shadow)'
                    }}>
                      {filteredBanks.length > 0 ? (
                        filteredBanks.map((setting) => (
                          <div
                            key={setting.id}
                            onClick={() => handleBankSelect(setting)}
                            className="search-result-item"
                            style={{ padding: '0.75rem 1rem' }}
                          >
                            <div className="search-result-title" style={{ fontWeight: 500 }}>
                              {setting.name}
                            </div>
                            <div className="search-result-meta" style={{ fontSize: '0.75rem' }}>
                              Interest: {setting.interestRate}%
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="search-result-empty" style={{ fontSize: '0.875rem' }}>
                          No banks found. Type to add new bank.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {formData.pledgedTo && !pledgeSettings.find(s => 
                  s.name.toLowerCase() === formData.pledgedTo.toLowerCase()
                ) && (
                  <div className="create-pledge-hint-ok">
                    ✓ Custom bank name - please enter interest rate manually
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{texts.interestRate || 'Interest Rate (% per month)'} *</label>
                <input
                  type="number"
                  name="interestRate"
                  className="form-input"
                  placeholder={texts.interestRatePlaceholder || "Enter interest rate"}
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  required
                />
                {formData.pledgedTo && pledgeSettings.find(s => 
                  s.name.toLowerCase() === formData.pledgedTo.toLowerCase()
                ) && (
                  <div className="create-pledge-hint-ok">
                    ✓ Auto-filled from bank settings
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
            <button type="submit" className="btn btn-primary" disabled={isSaving || isUploading}>
              <Save size={16} />
              {isUploading ? (texts.buttons?.uploading || 'Uploading...') : isSaving ? (texts.buttons?.creating || 'Saving...') : (texts.buttons?.createPledge || 'Create Pledge')}
            </button>
          </div>
        </form>
      </div>
      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}
    </div>
  );
};

export default CreatePledge;
